import type { SegmentW } from './hiddenLines'
import {
  dot,
  sub,
  viewBasis,
  type Crop2D,
  type Point2,
  type Polyline2D,
  type Segment2,
  type Segment3,
  type Vec3,
  type View2DDef,
  type ViewBasis,
} from './viewTypes'

// ---------------------------------------------------------------------------
// Projecção do mundo para o plano do desenho.
//
//   x = (p - origin) · right
//   y = (p - origin) · up
//   w = (p - origin) · viewDir     (profundidade; 0 = plano de corte)
//
// O w serve para dois fins: filtrar o que está dentro da profundidade da
// vista (0 <= w <= depth) e ordenar por profundidade quando for preciso
// (fase 2, se algum dia houver remoção de linhas escondidas).
// ---------------------------------------------------------------------------

export interface ProjectedPoint {
  x: number
  y: number
  /** Profundidade ao longo do viewDir, em metros. */
  w: number
}

function toBasis(v: View2DDef | ViewBasis): ViewBasis {
  return 'viewDir' in v ? v : viewBasis(v)
}

/** Projecta um ponto do mundo. */
export function projectPoint(
  p: Vec3,
  view: View2DDef | ViewBasis,
): ProjectedPoint {
  const b = toBasis(view)
  const d = sub(p, b.origin)
  return { x: dot(d, b.right), y: dot(d, b.up), w: dot(d, b.viewDir) }
}

/** Projecta uma lista de pontos do mundo. Aceita `Vec3[]` ou um
 *  Float32Array com xyz intercalado. */
export function projectToView(
  points: Vec3[] | Float32Array | number[],
  view: View2DDef | ViewBasis,
): ProjectedPoint[] {
  const b = toBasis(view)
  const out: ProjectedPoint[] = []
  if (Array.isArray(points) && points.length > 0 && Array.isArray(points[0])) {
    for (const p of points as Vec3[]) out.push(projectPoint(p, b))
    return out
  }
  const flat = points as Float32Array | number[]
  for (let i = 0; i + 2 < flat.length; i += 3) {
    out.push(projectPoint([flat[i], flat[i + 1], flat[i + 2]], b))
  }
  return out
}

/** Projecta segmentos do mundo, sem filtrar profundidade (usa-se para as
 *  linhas de corte, que estão todas em w = 0). */
export function projectSegments(
  segments: Segment3[],
  view: View2DDef | ViewBasis,
): Segment2[] {
  const b = toBasis(view)
  const out: Segment2[] = []
  for (const s of segments) {
    const a = projectPoint(s.a, b)
    const c = projectPoint(s.b, b)
    out.push({ a: [a.x, a.y], b: [c.x, c.y] })
  }
  return out
}

/** Projecta segmentos aparando-os à laje de profundidade da vista
 *  (near <= w <= far). Segmentos inteiramente fora saem fora; os que
 *  atravessam a fronteira saem cortados no ponto exacto.
 *
 *  `near` é 0 por omissão (nada aquém do plano de corte: o observador
 *  não vê o que está para trás de si) e `far` é a profundidade da vista.
 *  Passar `far: Infinity` desliga o corte ao fundo. */
export function projectSegmentsClipped(
  segments: Segment3[],
  view: View2DDef | ViewBasis,
  opts?: { near?: number; far?: number },
): Segment2[] {
  const b = toBasis(view)
  const near = opts?.near ?? 0
  const far = opts?.far ?? b.depth
  const out: Segment2[] = []
  for (const s of segments) {
    const p = projectPoint(s.a, b)
    const q = projectPoint(s.b, b)
    const clipped = clipByDepth(p, q, near, far)
    if (clipped) out.push(clipped)
  }
  return out
}

/** Como o `projectSegmentsClipped`, mas GUARDA a profundidade dos
 *  extremos. É o que a remoção de linhas ocultas precisa: sem o w não há
 *  como decidir se a aresta está à frente ou atrás do que o mapa de
 *  profundidade guardou naquele ponto. */
export function projectSegmentsClippedW(
  segments: Segment3[],
  view: View2DDef | ViewBasis,
  opts?: { near?: number; far?: number },
): SegmentW[] {
  const b = toBasis(view)
  const near = opts?.near ?? 0
  const far = opts?.far ?? b.depth
  const out: SegmentW[] = []
  for (const s of segments) {
    const p = projectPoint(s.a, b)
    const q = projectPoint(s.b, b)
    const clipped = clipByDepthW(p, q, near, far)
    if (clipped) out.push(clipped)
  }
  return out
}

/** Apara um segmento projectado ao intervalo de profundidade,
 *  devolvendo também o w dos extremos aparados. */
export function clipByDepthW(
  p: ProjectedPoint,
  q: ProjectedPoint,
  near: number,
  far: number,
): SegmentW | null {
  const t = depthSpan(p, q, near, far)
  if (!t) return null
  const [t0, t1] = t
  const ax = p.x + (q.x - p.x) * t0
  const ay = p.y + (q.y - p.y) * t0
  const bx = p.x + (q.x - p.x) * t1
  const by = p.y + (q.y - p.y) * t1
  if (ax === bx && ay === by) return null
  return {
    a: [ax, ay],
    b: [bx, by],
    wa: p.w + (q.w - p.w) * t0,
    wb: p.w + (q.w - p.w) * t1,
  }
}

/** Apara um segmento projectado ao intervalo de profundidade. */
export function clipByDepth(
  p: ProjectedPoint,
  q: ProjectedPoint,
  near: number,
  far: number,
): Segment2 | null {
  const span = depthSpan(p, q, near, far)
  if (!span) return null
  const [t0, t1] = span
  const ax = p.x + (q.x - p.x) * t0
  const ay = p.y + (q.y - p.y) * t0
  const bx = p.x + (q.x - p.x) * t1
  const by = p.y + (q.y - p.y) * t1
  if (ax === bx && ay === by) return null
  return { a: [ax, ay], b: [bx, by] }
}

/** Intervalo de parâmetro [t0, t1] em que o segmento p->q respeita
 *  near <= w <= far, ou null se ficar todo fora. */
function depthSpan(
  p: ProjectedPoint,
  q: ProjectedPoint,
  near: number,
  far: number,
): [number, number] | null {
  let t0 = 0
  let t1 = 1
  const dw = q.w - p.w
  // w(t) = p.w + t*dw, com t em [0,1]. Duas restrições:
  //   w >= near  ->  -dw*t <= p.w - near
  //   w <= far   ->   dw*t <= far - p.w
  const clip = (den: number, num: number): boolean => {
    if (Math.abs(den) < 1e-15) return num >= 0
    const r = num / den
    if (den < 0) {
      if (r > t1) return false
      if (r > t0) t0 = r
    } else {
      if (r < t0) return false
      if (r < t1) t1 = r
    }
    return true
  }
  if (!clip(-dw, p.w - near)) return null
  if (!clip(dw, far - p.w)) return null
  // Segmento reduzido a um ponto (tangente à fronteira).
  if (t1 <= t0) return null
  return [t0, t1]
}

// --- Recorte à janela (crop) ----------------------------------------------

/** Recorta um segmento 2D ao rectângulo (Liang-Barsky). */
export function clipSegmentToRect(
  a: Point2,
  b: Point2,
  rect: Crop2D,
): Segment2 | null {
  let t0 = 0
  let t1 = 1
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const tests: Array<[number, number]> = [
    [-dx, a[0] - rect.minX],
    [dx, rect.maxX - a[0]],
    [-dy, a[1] - rect.minY],
    [dy, rect.maxY - a[1]],
  ]
  for (const [den, num] of tests) {
    if (Math.abs(den) < 1e-15) {
      if (num < 0) return null
      continue
    }
    const r = num / den
    if (den < 0) {
      if (r > t1) return null
      if (r > t0) t0 = r
    } else {
      if (r < t0) return null
      if (r < t1) t1 = r
    }
  }
  const ax = a[0] + dx * t0
  const ay = a[1] + dy * t0
  const bx = a[0] + dx * t1
  const by = a[1] + dy * t1
  if (ax === bx && ay === by) return null
  return { a: [ax, ay], b: [bx, by] }
}

/** Recorta uma polilinha ao rectângulo. Uma polilinha que entre e saia
 *  da janela parte-se em vários troços ABERTOS (uma polilinha fechada
 *  recortada deixa de estar fechada, e não se inventa fecho). */
export function clipPolylineToRect(
  pl: Polyline2D,
  rect: Crop2D,
): Polyline2D[] {
  const pts = pl.points
  if (pts.length < 2) return []
  const segs: Array<[Point2, Point2]> = []
  for (let i = 0; i + 1 < pts.length; i++) segs.push([pts[i], pts[i + 1]])
  if (pl.closed) segs.push([pts[pts.length - 1], pts[0]])

  // Dentro por inteiro: caminho rápido, preserva o fecho.
  let allInside = true
  for (const p of pts) {
    if (
      p[0] < rect.minX ||
      p[0] > rect.maxX ||
      p[1] < rect.minY ||
      p[1] > rect.maxY
    ) {
      allInside = false
      break
    }
  }
  if (allInside) return [pl]

  const out: Polyline2D[] = []
  let cur: Point2[] = []
  const flush = (): void => {
    if (cur.length >= 2) out.push({ points: cur, closed: false })
    cur = []
  }
  for (const [a, b] of segs) {
    const c = clipSegmentToRect(a, b, rect)
    if (!c) {
      flush()
      continue
    }
    if (cur.length === 0) {
      cur.push(c.a, c.b)
    } else {
      const last = cur[cur.length - 1]
      if (Math.abs(last[0] - c.a[0]) < 1e-12 && Math.abs(last[1] - c.a[1]) < 1e-12) {
        cur.push(c.b)
      } else {
        flush()
        cur.push(c.a, c.b)
      }
    }
  }
  flush()
  return out
}
